import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo10Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 10 BWV 781
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV781/bach-invention-10/bach-invention-10-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 10 BWV 781.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4847">
      <Navbar data-id="element-4848" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4849">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4850">
          <img src="https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=400&h=300&fit=crop" alt="Invention No. 10" className="w-full h-64 md:h-80 object-cover" data-id="element-4851" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4852" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4853">
            <div className="max-w-7xl mx-auto" data-id="element-4854">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4855">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4856" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4857">
                Invention No. 10 in G major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4858">BWV 781</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4859">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4860">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4861">
          <div className="flex items-center space-x-2" data-id="element-4862">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4863">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4864" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4865">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4866">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4867" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4868">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4869">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4870" />
              <span className="hidden sm:inline" data-id="element-4871">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4872">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4873" />
              <span className="hidden sm:inline" data-id="element-4874">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4875">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4876">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4877">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 10 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4878" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4879">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4880">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4881">
            <div data-id="element-4882">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4883">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4884">
                <div data-id="element-4885">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4886">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4887">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4888">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4889">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4890">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-4891">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4892">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4893">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-4894">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4895">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4896">
                    G major
                  </dd>
                </div>
                <div data-id="element-4897">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4898">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4899">
                    BWV 781
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4900">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4901">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4902">
                The tenth of Bach's fifteen Two-Part Inventions, this piece in G
                major is celebrated for its brilliant, virtuosic character and
                sparkling energy. Written as pedagogical works for keyboard
                students, the Inventions teach the art of two-voice counterpoint
                while developing musical ideas through systematic elaboration
                and imitation. This invention features a lively, ascending
                subject that creates an immediate sense of brightness and
                forward motion, with rapid sixteenth-note passages that demand
                both technical precision and musical clarity from the performer.
                The major key gives the work a joyful, celebratory quality that
                makes it one of the most popular and frequently performed
                inventions in the collection. The piece demonstrates Bach's
                mastery of invertible counterpoint, with the two voices engaging
                in a spirited dialogue that maintains perfect balance while
                creating a sense of playful competition. Technical challenges
                include maintaining evenness and clarity in the rapid scalar
                passages, managing the hand crossings that occur when the voices
                intersect, executing clean articulation in the staccato
                sections, and bringing out the contrapuntal lines while
                maintaining the overall sense of brilliance and vitality. This
                invention serves as both an essential technical study in baroque
                keyboard virtuosity and a delightful musical showcase that
                demonstrates Bach's ability to combine pedagogical purpose with
                genuine artistic expression, creating a work that is as
                enjoyable to perform as it is instructive.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4903" />
    </div>;
};